import { Logo } from "@/components/common/Logo/Logo"
import {
  TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { Heading, Text, Theme, View } from "@aws-amplify/ui-react"
import "@aws-amplify/ui-react/styles.css"

export const CustomHeader = () => {
  const { translate } = useTranslation()
  return (
    <View textAlign="center" className="pb-6">
      <div className="h-12">
        <Logo />
      </div>
      <p className="mt-2 text-sm tracking-wide text-grey5">
        {translate("LOGIN/TAGLINE")}
      </p>
    </View>
  )
}

export const getCustomFormFields = (
  translate: (key: TranslationKeys) => string,
  preFilledEmail?: string,
  preFilledName?: string,
) => {
  // Amplify's password field hard-codes English screen reader labels; they are
  // props of the field, so they come from our catalogs.
  const passwordLabels = {
    showPasswordButtonLabel: translate("LOGIN/SHOW_PASSWORD"),
    passwordIsHiddenLabel: translate("LOGIN/PASSWORD_HIDDEN"),
    passwordIsShownLabel: translate("LOGIN/PASSWORD_SHOWN"),
  }
  return {
    signIn: {
      username: {
        label: translate("LOGIN/EMAIL"),
        type: "email",
        placeholder: translate("LOGIN/EMAIL_PLACEHOLDER"),
        isRequired: true,
        order: 1,
        defaultValue: preFilledEmail,
      },
      password: {
        label: translate("LOGIN/PASSWORD"),
        placeholder: translate("LOGIN/PASSWORD_PLACEHOLDER"),
        isRequired: true,
        order: 2,
        ...passwordLabels,
      },
    },
    signUp: {
      username: {
        label: translate("LOGIN/EMAIL"),
        type: "email",
        placeholder: translate("LOGIN/EMAIL_PLACEHOLDER"),
        isRequired: true,
        order: 1,
        defaultValue: preFilledEmail,
      },
      name: {
        label: translate("SIGNUP/NAME"),
        placeholder: translate("SIGNUP/NAME_PLACEHOLDER"),
        isRequired: true,
        order: 2,
        defaultValue: preFilledName,
      },
      password: {
        placeholder: translate("LOGIN/PASSWORD_PLACEHOLDER"),
        ...passwordLabels,
      },
      confirm_password: {
        placeholder: translate("LOGIN/CONFIRM_NEW_PASSWORD_PLACEHOLDER"),
        ...passwordLabels,
      },
    },
    forgotPassword: {
      username: {
        label: translate("LOGIN/EMAIL"),
        type: "email",
        placeholder: translate("LOGIN/EMAIL_PLACEHOLDER"),
        isRequired: true,
      },
    },
    forceNewPassword: {
      password: {
        label: translate("LOGIN/NEW_PASSWORD"),
        placeholder: translate("LOGIN/NEW_PASSWORD_PLACEHOLDER"),
        ...passwordLabels,
      },
      confirm_password: {
        label: translate("LOGIN/CONFIRM_NEW_PASSWORD"),
        placeholder: translate("LOGIN/CONFIRM_NEW_PASSWORD_PLACEHOLDER"),
        ...passwordLabels,
      },
    },
    confirmResetPassword: {
      username: {
        label: translate("LOGIN/EMAIL"),
        type: "email",
        placeholder: translate("LOGIN/EMAIL_PLACEHOLDER"),
        isRequired: true,
      },
      password: {
        placeholder: translate("LOGIN/NEW_PASSWORD_PLACEHOLDER"),
        ...passwordLabels,
      },
      confirm_password: {
        placeholder: translate("LOGIN/CONFIRM_NEW_PASSWORD_PLACEHOLDER"),
        ...passwordLabels,
      },
    },
  }
}

export const ForceNewPasswordHeader = () => {
  const { translate } = useTranslation()
  return (
    <View textAlign="left" className="pb-2">
      <Heading level={3}>{translate("LOGIN/NEW_PASSWORD_TITLE")}</Heading>
      <Text className="mt-2 text-sm text-grey5">
        {translate("LOGIN/NEW_PASSWORD_INTRO")}
      </Text>
      <Text className="mt-2 text-sm text-grey5">
        {translate("LOGIN/NEW_PASSWORD_RULES")}
      </Text>
    </View>
  )
}

export const CustomTheme: Theme = {
  name: "custom-theme",
  tokens: {
    colors: {
      background: {
        primary: "transparent",
      },
      border: {
        primary: "rgba(255, 255, 255, 0.1)",
        focus: "rgba(255, 255, 255, 0.25)",
        hover: "rgba(255, 255, 255, 0.2)",
      },
      font: {
        primary: "#FFF",
        secondary: "#A5A5A5",
      },
      brand: {
        primary: {
          10: "#FFF440",
          80: "#000",
        },
      },
      link: {
        primary: "rgba(255, 255, 255, 0.6)",
        hover: "#FFF",
      },
    },
    components: {
      button: {
        primary: {
          backgroundColor: "{colors.brand.primary.10}",
          color: "{colors.brand.primary.80}",
          _hover: {
            backgroundColor: "#FFF99B",
            color: "{colors.brand.primary.80}",
          },
        },
        link: {
          color: "{colors.link.primary}",
          _hover: {
            color: "{colors.link.hover}",
          },
        },
      },
      fieldcontrol: {
        borderColor: "{colors.border.primary}",
        _focus: {
          borderColor: "{colors.border.focus}",
        },
      },
    },
  },
}
